import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { arrayBufferToBase64, base64ToArrayBuffer, decryptAES, deriveWrapKey, importWrapKey } from '../lib/crypto';
import { t } from '../lib/i18n';
import { setAccountIdentityKey } from '../lib/session';
import { api } from '../lib/trpc/client';

export function UnlockPage() {
  const navigate = useNavigate();
  const [passphrase, setPassphrase] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const hasSession = Boolean(localStorage.getItem('accessToken') && localStorage.getItem('userId'));
    if (!hasSession) {
      navigate('/', { replace: true });
      return;
    }

    if (localStorage.getItem('identitySkRaw')) {
      navigate('/circles', { replace: true });
    }
  }, [navigate]);

  const handleUnlock = async (event: FormEvent) => {
    event.preventDefault();
    if (!passphrase) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const envelope = await api.auth.getKeyEnvelope();
      const { wrapKey } = await deriveWrapKey(passphrase, new Uint8Array(base64ToArrayBuffer(envelope.argon2Salt)));
      const wrapKeyObj = await importWrapKey(wrapKey);
      const decrypted = await decryptAES(
        wrapKeyObj,
        base64ToArrayBuffer(envelope.encryptedSk),
        new Uint8Array(base64ToArrayBuffer(envelope.nonce))
      );

      const identitySkRaw = arrayBufferToBase64(decrypted);
      localStorage.setItem('identitySkRaw', identitySkRaw);

      const userId = localStorage.getItem('userId');
      if (userId) {
        setAccountIdentityKey(userId, identitySkRaw);
      }

      navigate('/circles', { replace: true });
    } catch {
      setError(t('パスフレーズが正しくないか、鍵の復元に失敗しました。', 'Passphrase is incorrect or key recovery failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md border-border/80">
        <CardHeader>
          <CardTitle>{t('秘密鍵を復元', 'Unlock private key')}</CardTitle>
          <CardDescription>
            {t('この端末には秘密鍵がありません。パスフレーズを入力して復元してください。', 'This device does not have your private key yet. Enter your passphrase to restore it.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <form className="space-y-3" onSubmit={handleUnlock}>
            <div className="space-y-2">
              <Label htmlFor="passphrase">{t('パスフレーズ', 'Passphrase')}</Label>
              <Input
                id="passphrase"
                type="password"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={submitting || !passphrase}>
              {submitting ? t('復元中...', 'Unlocking...') : t('復元して続行', 'Unlock and continue')}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            <Link className="underline decoration-dotted underline-offset-4" to="/login">
              {t('別のアカウントでログイン', 'Login with another account')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
